import { ArrowBack, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Paper,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChecklistTask } from "../../common/ChecklistTask";
import {
  JobRating,
  JobRatingLabel,
  Publish,
  PublishLabel,
} from "../../common/JobOrder";
import { User, UserGroup } from "../../common/User";
import { Vessel } from "../../common/Vessel";
import { useGetJobOrderQuery } from "../../redux/api/jobOrder";
import convertDateToString from "../../util/ConvertDateToString";
import useDocumentTitle from "../../util/useDocumentTitle";
import DefaultJobRequirementsDisplay from "./DefaultJobRequirementsDisplay";
import "./JobBoardPostingComponent.scss";
import { formatDateString } from "../../common/FormatDateString";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectCurrentProfile,
} from "../../redux/authentication/authSlice";
import { MarinerType } from "../../common/MarinerProfile";
import {
  useApplyMutation,
  useCheckAppliedQuery,
} from "../../redux/api/jobApplication";
import { toast } from "react-toastify";

type job = {
  date: string;
  vessel: Vessel | undefined;
  assignee: User | undefined;
  rating: string;
  port: string;
  offsigner: string;
  onsigner: string;
  checklistRoot: ChecklistTask;
  publish: Publish | undefined;
};

const JobBoardPostingComponent = () => {
  useDocumentTitle("Job Posting");
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const currentProfile = useSelector(selectCurrentProfile);
  const userGroup = user?.userGroup as UserGroup;
  const isAMO = (currentProfile?.type as MarinerType) === MarinerType.LICENSED;
  const isViewOnly =
    userGroup === UserGroup.CREWING_EXECS ||
    userGroup === UserGroup.HUMAN_RESOURCES;
  const [jobOrder, setJobOrder] = useState<job | null>(null);
  const [confirmApply, setConfirmApply] = useState(false);
  const { id } = useParams();
  const { data: jobOrderFromApi, isLoading: isJobOrdersLoading } =
    useGetJobOrderQuery(id !== undefined ? id : skipToken);
  const {
    data: isApplied,
    isLoading: isCheckAppliedLoading,
    refetch,
  } = useCheckAppliedQuery(
    { appliedById: currentProfile?.id, jobOrderId: jobOrderFromApi?.id },
    {
      skip:
        !currentProfile ||
        !currentProfile?.id ||
        !jobOrderFromApi ||
        !jobOrderFromApi.id ||
        !isAMO,
    }
  );

  const [apply] = useApplyMutation();

  const handleApply = () => {
    setConfirmApply(true);
  };

  const handleClose = () => {
    setConfirmApply(false);
  };

  const submitApplication = async () => {
    const response = await apply({
      appliedById: currentProfile?.id,
      jobOrderId: jobOrderFromApi?.id,
    });
    refetch();
    if ("error" in response === false) {
      toast.success(<p>Applied successfully.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      handleClose();
    }
  };

  useEffect(() => {
    if (!!jobOrderFromApi) {
      let res: any = JSON.parse(JSON.stringify(jobOrderFromApi));
      Object.keys(res).forEach((key) => {
        res[key] = res[key] === null ? "" : res[key];
      });

      setJobOrder({
        date:
          res["fillByDate"] !== undefined && res["fillByDate"] !== ""
            ? convertDateToString(new Date(res["fillByDate"]))
            : res["fillByDate"],
        vessel: res["vessel"],
        assignee: res["assignee"],
        onsigner: res["onsigner"],
        offsigner: res["offsigner"],
        checklistRoot: res["checklistRoot"],
        rating: res["rating"],
        port: res["port"],
        publish: res["publish"],
      });
    }
  }, [jobOrderFromApi]);

  if (isJobOrdersLoading || isCheckAppliedLoading) {
    <Box sx={{ display: "flex" }}>
      <CircularProgress />
    </Box>;
  }

  return (
    <>
      {!!jobOrder?.publish &&
        (jobOrder?.publish === Publish.OPEN ||
          jobOrder?.publish === Publish.FILLING) && (
          <div className="page-wrap">
            <div className="page-header-between job-header-width align-items-center">
              <div className="header-group">
                <h1>Job Order</h1>
                {isAMO && (
                  <Button
                    variant="contained"
                    className={isApplied ? "grey-button" : "blue-button"}
                    onClick={handleApply}
                    disabled={isApplied}
                  >
                    {isApplied ? "Applied" : "Apply"}
                  </Button>
                )}
                <Dialog
                  open={confirmApply}
                  onClose={handleClose}
                  aria-labelledby="modal-title"
                >
                  <Typography variant="h3" id="modal-title">
                    Apply for Job
                  </Typography>
                  <p className="delete-paragraph">
                    Are you sure you want to apply for this job?
                  </p>
                  <div className="modal-footer">
                    <Button
                      className="outline-button-small"
                      onClick={submitApplication}
                    >
                      Apply
                    </Button>
                    <Button className="blue-button-small" onClick={handleClose}>
                      Cancel
                    </Button>
                  </div>
                </Dialog>
              </div>
              <Button className="blue-link-button" onClick={() => navigate(-1)}>
                {" "}
                <ArrowBack />
                Back
              </Button>
            </div>
            <main>
              {!isViewOnly && userGroup !== UserGroup.MARINERS && (
                <div className="info-alert">
                  <span>
                    You are viewing a job board posting. To view and edit the
                    full job order,
                    {
                      <Link
                        className="blue-link inline-link"
                        to={`/jobOrders/${id}`}
                      >
                        click here
                      </Link>
                    }
                  </span>
                </div>
              )}
              {!isViewOnly && userGroup === UserGroup.MARINERS && !isAMO && (
                <div className="info-alert">
                  <span className="flex gc-1">
                    <InfoOutlined color="info" /> Contact your SIU hall if you
                    are interested in this job.
                  </span>
                </div>
              )}
              {!isViewOnly && userGroup === UserGroup.MARINERS && (
                <div className="info-alert">
                  <span className="flex gc-1">
                    <InfoOutlined color="info" /> TOTE Services requires all
                    merchant mariner documents have 6 month validity at joining.
                    Merchant mariner documents include Passport, MMC, TWIC and
                    medical certificate.
                  </span>
                </div>
              )}
              <div className="posting-content">
                <div className="posting-column">
                  <div>
                    <DefaultJobRequirementsDisplay />
                  </div>
                </div>
                <div className="posting-details">
                  <Paper className="flex-col g-1">
                    <h3>Job Order Details</h3>
                    <div className="form-wide">
                      <label>
                        <span>Job Order ID</span>
                        {id}
                      </label>
                      <label>
                        <span>Rating</span>
                        {!!jobOrder?.rating &&
                          JobRatingLabel[
                            JobRating[jobOrder.rating as keyof typeof JobRating]
                          ]}
                      </label>
                      <label>
                        <span>Port</span>
                        {jobOrder?.port}
                      </label>
                      <label>
                        <span>Start Date</span>
                        {formatDateString(jobOrder?.date)}
                      </label>
                      <label>
                        <span>Vessel</span>
                        {jobOrder?.vessel?.name}
                      </label>
                      <label>
                        <span>Status</span>
                        {PublishLabel[jobOrder?.publish as Publish]}
                      </label>
                    </div>
                  </Paper>
                </div>
              </div>
            </main>
          </div>
        )}
    </>
  );
};

export default JobBoardPostingComponent;
